'use client'

import React, {useCallback, useEffect, useLayoutEffect, useRef} from 'react'
import {createPortal} from 'react-dom'
import {AnimatePresence, usePresence, useReducedMotion} from 'framer-motion'
import {cn} from './cn'

// Drag model and timing follow Vaul (emilkowal.ski/ui/building-a-drawer-component).
// Settled motion runs as CSS transitions, which the compositor keeps smooth while the main thread is busy.
const SHEET_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const SHEET_DURATION_MS = 500
// Leaving is faster than arriving, so a dismissal never holds up the screen below.
const SHEET_EXIT_DURATION_MS = 350
const FADE_TRANSITION = '200ms cubic-bezier(0.23, 1, 0.32, 1)'
const FADE_DURATION_MS = 200
// A release faster than this (px/ms) or past this share of the height closes the sheet.
const VELOCITY_THRESHOLD = 0.4
// Any upward push faster than this (px/ms) at release cancels the close.
const UPWARD_CANCEL_VELOCITY = 0.11
const CLOSE_THRESHOLD = 0.25
// Release velocity is measured over this trailing window, so a pause before letting go cancels a flick.
const VELOCITY_WINDOW_MS = 100
// Content scrolled within this window keeps the gesture a scroll.
const SCROLL_LOCK_TIMEOUT_MS = 100
// Lets a scroll that starts while the sheet is still entering stay a scroll.
const OPEN_DRAG_DELAY_MS = 500
const NO_DRAG_SELECTOR = '[data-sheet-no-drag]'

const dampen = (distance: number) => 8 * (Math.log(distance + 1) - 2)
// Inverse of the rubber band, so grabbing a sheet above rest does not damp its offset twice.
const undampen = (visual: number) => Math.exp(visual / 8 + 2) - 1

const applyViewportGeometry = (sheet: HTMLElement | null) => {
    if (!sheet) return

    const viewport = window.visualViewport
    const viewportHeight = viewport?.height ?? window.innerHeight
    const viewportWidth = viewport?.width ?? window.innerWidth
    const viewportTop = viewport?.offsetTop ?? 0
    const viewportLeft = viewport?.offsetLeft ?? 0
    const viewportBottom = Math.max(0, window.innerHeight - viewportTop - viewportHeight)

    sheet.style.setProperty('--action-sheet-viewport-height', `${viewportHeight}px`)
    sheet.style.setProperty('--action-sheet-viewport-width', `${viewportWidth}px`)
    sheet.style.setProperty('--action-sheet-viewport-left', `${viewportLeft}px`)
    sheet.style.setProperty('--action-sheet-viewport-bottom', `${viewportBottom}px`)
}

type Layer = {sheet: HTMLElement; backdrop: HTMLElement}

// Moves the sheet `offset` px below its resting point; the backdrop fades in step.
const place = ({sheet, backdrop}: Layer, offset: number, height: number, animated: boolean, duration = SHEET_DURATION_MS) => {
    const transition = animated ? `${duration}ms ${SHEET_EASE}` : '0s'
    sheet.style.transition = `transform ${transition}`
    sheet.style.transform = `translate3d(0, ${offset}px, 0)`
    backdrop.style.transition = `opacity ${transition}`
    backdrop.style.opacity = String(Math.min(1, Math.max(0, 1 - offset / height)))
}

// Where the sheet is right now, including mid-transition, so a grab continues from there.
const liveOffset = (sheet: HTMLElement) => {
    const transform = getComputedStyle(sheet).transform
    return transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0
}

// Keeps one anchor sample at or before the window edge, so a jitter after a hold cannot read as a flick.
const trimSamples = (samples: {time: number}[], time: number) => {
    while (samples.length > 1 && samples[1].time <= time - VELOCITY_WINDOW_MS) samples.shift()
}

// Reduced motion swaps the slide for a fade.
const fade = ({sheet, backdrop}: Layer, visible: boolean) => {
    for (const element of [sheet, backdrop]) {
        element.style.transition = `opacity ${FADE_TRANSITION}`
        element.style.opacity = visible ? '1' : '0'
    }
}

type PanelProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'style'>

export type BottomSheetProps = PanelProps & {
    open: boolean
    onClose: () => void
    container: Element
    panelRef?: React.Ref<HTMLDivElement>
    panelZIndex?: number
    backdropZIndex?: number
    onBackdropClick?: () => void
    onBackdropPointerUp?: (event: React.PointerEvent<HTMLElement>) => void
    children: React.ReactNode
}

/**
 * A bottom sheet that slides in, follows the finger, and closes on a flick or a long pull.
 * Focus, Escape and scroll lock stay with the caller and `@fitro/modal-routing`.
 * Elements marked `data-sheet-no-drag` never start a drag.
 */
export function BottomSheet({open, container, ...props}: BottomSheetProps) {
    return createPortal(<AnimatePresence>{open && <SheetLayer {...props}/>}</AnimatePresence>, container)
}

type Gesture = {
    pointerId: number
    startX: number
    startY: number
    dragging: boolean
    // Sheet offset when the drag took over.
    baseOffset: number
    samples: {y: number; time: number}[]
}

function SheetLayer({
                        onClose,
                        panelRef,
                        panelZIndex,
                        backdropZIndex,
                        onBackdropClick,
                        onBackdropPointerUp,
                        className,
                        children,
                        ...panelProps
                    }: Omit<BottomSheetProps, 'open' | 'container'>) {
    const [isPresent, safeToRemove] = usePresence()
    const reduceMotion = Boolean(useReducedMotion())
    const sheetRef = useRef<HTMLDivElement>(null)
    const backdropRef = useRef<HTMLDivElement>(null)
    const offsetRef = useRef(0)
    const heightRef = useRef(window.innerHeight)
    const gestureRef = useRef<Gesture | null>(null)
    const openedAtRef = useRef(0)
    const scrollPreventedAtRef = useRef(0)
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose

    // Stable, so re-renders during the exit do not detach and reattach the caller's ref.
    const setRefs = useCallback((node: HTMLDivElement | null) => {
        sheetRef.current = node
        if (typeof panelRef === 'function') panelRef(node)
        else if (panelRef) panelRef.current = node
    }, [panelRef])

    const layer = (): Layer | null => sheetRef.current && backdropRef.current
        ? {sheet: sheetRef.current, backdrop: backdropRef.current}
        : null
    const moveTo = (offset: number, animated: boolean) => {
        offsetRef.current = offset
        const current = layer()
        if (current) place(current, offset, heightRef.current, animated && !reduceMotion)
    }

    // Runs before the first paint, so the sheet never flashes in place.
    useLayoutEffect(() => {
        const sheet = sheetRef.current
        const backdrop = backdropRef.current
        if (!sheet || !backdrop) return

        applyViewportGeometry(sheet)
        openedAtRef.current = performance.now()
        heightRef.current = sheet.offsetHeight || window.innerHeight
        if (reduceMotion) {
            fade({sheet, backdrop}, false)
            sheet.getBoundingClientRect()
            fade({sheet, backdrop}, true)
            return
        }
        place({sheet, backdrop}, heightRef.current, heightRef.current, false)
        // Commits the start position so the transition runs from below the viewport.
        sheet.getBoundingClientRect()
        place({sheet, backdrop}, 0, heightRef.current, true)
    }, [reduceMotion])

    useEffect(() => {
        const sheet = sheetRef.current
        const backdrop = backdropRef.current
        if (isPresent || !sheet || !backdrop) return

        gestureRef.current = null
        if (reduceMotion) fade({sheet, backdrop}, false)
        else place({sheet, backdrop}, sheet.offsetHeight || heightRef.current, heightRef.current, true, SHEET_EXIT_DURATION_MS)
        const removal = window.setTimeout(safeToRemove, reduceMotion ? FADE_DURATION_MS : SHEET_EXIT_DURATION_MS)
        return () => {
            window.clearTimeout(removal)
            // Reopened mid-exit: the transition runs back from where the exit stopped.
            offsetRef.current = 0
            if (reduceMotion) fade({sheet, backdrop}, true)
            else place({sheet, backdrop}, 0, heightRef.current, true)
        }
    }, [isPresent, reduceMotion, safeToRemove])

    useEffect(() => {
        const updateViewportGeometry = () => applyViewportGeometry(sheetRef.current)
        const viewport = window.visualViewport
        viewport?.addEventListener('resize', updateViewportGeometry, {passive: true})
        viewport?.addEventListener('scroll', updateViewportGeometry, {passive: true})
        window.addEventListener('resize', updateViewportGeometry, {passive: true})
        window.addEventListener('orientationchange', updateViewportGeometry, {passive: true})

        return () => {
            viewport?.removeEventListener('resize', updateViewportGeometry)
            viewport?.removeEventListener('scroll', updateViewportGeometry)
            window.removeEventListener('resize', updateViewportGeometry)
            window.removeEventListener('orientationchange', updateViewportGeometry)
        }
    }, [])

    useEffect(() => {
        const sheet = sheetRef.current
        if (!sheet) return

        // Once a drag owns the touch, the browser must neither scroll the content nor cancel the pointer.
        const blockScroll = (event: TouchEvent) => {
            if (gestureRef.current?.dragging && event.cancelable) event.preventDefault()
        }
        sheet.addEventListener('touchmove', blockScroll, {passive: false})
        return () => sheet.removeEventListener('touchmove', blockScroll)
    }, [])

    // Mirrors Vaul's shouldDrag: content that can still scroll up keeps the gesture.
    const canDrag = (target: Element, movingUp: boolean, offset: number) => {
        const now = performance.now()
        if (target.tagName === 'SELECT' || target.closest(NO_DRAG_SELECTOR)) return false
        if (now - openedAtRef.current < OPEN_DRAG_DELAY_MS) return false
        // Sub-pixel settle tails count as rest so scroll keeps working.
        if (offset >= 1) return true
        if (window.getSelection()?.toString()) return false
        if (now - scrollPreventedAtRef.current < SCROLL_LOCK_TIMEOUT_MS || movingUp) {
            scrollPreventedAtRef.current = now
            return false
        }
        for (let element: Element | null = target; element && element !== sheetRef.current; element = element.parentElement) {
            if (element.scrollHeight > element.clientHeight && element.scrollTop !== 0) {
                scrollPreventedAtRef.current = now
                return false
            }
        }
        return true
    }

    const settle = () => moveTo(0, true)

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        panelProps.onPointerDown?.(event)
        if (!isPresent || event.isPrimary === false || gestureRef.current) return

        const sheet = sheetRef.current
        if (sheet) heightRef.current = sheet.offsetHeight || heightRef.current
        const pointerId = event.pointerId
        const target = event.target as Element
        const gesture: Gesture = {
            pointerId,
            startX: event.clientX,
            startY: event.clientY,
            dragging: false,
            baseOffset: 0,
            samples: [],
        }
        gestureRef.current = gesture
        const threshold = event.pointerType === 'touch' ? 10 : 2

        const move = (moveEvent: PointerEvent) => {
            if (moveEvent.pointerId !== pointerId || gestureRef.current !== gesture) return
            let deltaY = moveEvent.clientY - gesture.startY
            if (!gesture.dragging) {
                const deltaX = moveEvent.clientX - gesture.startX
                if (Math.abs(deltaX) <= threshold && Math.abs(deltaY) <= threshold) return
                // A sideways swipe belongs to horizontal scrollers inside the sheet.
                if (Math.abs(deltaX) > Math.abs(deltaY)) return end()
                const sheet = sheetRef.current
                const baseOffset = sheet && !reduceMotion ? liveOffset(sheet) : offsetRef.current
                if (!canDrag(target, deltaY < 0, baseOffset)) return
                gesture.dragging = true
                gesture.baseOffset = baseOffset < 0 ? -undampen(-baseOffset) : baseOffset
                sheetRef.current?.setAttribute('data-dragging', '')
                // The sheet starts following from here, so crossing the slop does not make it jump.
                gesture.startY = moveEvent.clientY
                deltaY = 0
            }
            gesture.samples.push({y: moveEvent.clientY, time: moveEvent.timeStamp})
            trimSamples(gesture.samples, moveEvent.timeStamp)
            const offset = gesture.baseOffset + deltaY
            // Above the resting point the sheet resists like a rubber band.
            moveTo(offset < 0 ? Math.min(-dampen(-offset), 0) : offset, false)
        }

        const release = (upEvent: PointerEvent) => {
            if (upEvent.pointerId !== pointerId || gestureRef.current !== gesture) return
            const wasDragging = gesture.dragging
            end()
            if (!wasDragging) return

            // A drag must not also activate the control it started on.
            const suppressClick = (clickEvent: MouseEvent) => {
                clickEvent.preventDefault()
                clickEvent.stopPropagation()
            }
            window.addEventListener('click', suppressClick, {capture: true, once: true})
            window.setTimeout(() => window.removeEventListener('click', suppressClick, {capture: true}), 0)

            const distance = upEvent.clientY - gesture.startY
            trimSamples(gesture.samples, upEvent.timeStamp)
            const anchor = gesture.samples[0]
            const velocity = anchor ? (upEvent.clientY - anchor.y) / Math.max(1, upEvent.timeStamp - anchor.time) : 0
            // A short reverse flick inside the window can average out; the peak catches it.
            const reversed = Math.max(...gesture.samples.map(sample => sample.y)) - upEvent.clientY > 8
            const visibleHeight = Math.min(heightRef.current, window.innerHeight)
            // An upward flick keeps the sheet open even past the close distance.
            if (distance > 0 && !reversed && velocity > -UPWARD_CANCEL_VELOCITY && (velocity > VELOCITY_THRESHOLD || offsetRef.current >= visibleHeight * CLOSE_THRESHOLD)) {
                onCloseRef.current()
                return
            }
            settle()
        }

        const cancel = (cancelEvent: PointerEvent) => {
            if (cancelEvent.pointerId !== pointerId || gestureRef.current !== gesture) return
            const wasDragging = gesture.dragging
            end()
            if (wasDragging) settle()
        }

        const end = () => {
            if (gestureRef.current === gesture) gestureRef.current = null
            sheetRef.current?.removeAttribute('data-dragging')
            window.removeEventListener('pointermove', move)
            window.removeEventListener('pointerup', release)
            window.removeEventListener('pointercancel', cancel)
        }

        window.addEventListener('pointermove', move)
        window.addEventListener('pointerup', release)
        window.addEventListener('pointercancel', cancel)
    }

    return (
        <>
            <div
                ref={backdropRef}
                aria-hidden="true"
                className={cn('fixed inset-0 bg-black/50 dark:bg-[var(--scrim)]', !isPresent && 'pointer-events-none')}
                style={{zIndex: backdropZIndex}}
                onClick={onBackdropClick}
                onPointerUp={onBackdropPointerUp}
            />
            <div
                {...panelProps}
                ref={setRefs}
                role={panelProps.role ?? 'dialog'}
                aria-modal={panelProps['aria-modal'] ?? true}
                onPointerDown={handlePointerDown}
                className={cn(
                    'fixed flex flex-col outline-hidden touch-none',
                    // Keeps sheet content beside the landscape notch; the body's inset padding does not reach fixed panels.
                    'pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]',
                    'rounded-t-2xl bg-white shadow-xl dark:bg-gray-900',
                    // Fills the gap below the sheet while it is pulled above its resting point.
                    'after:absolute after:inset-x-0 after:top-full after:h-[200%] after:bg-inherit',
                    'max-h-[calc(var(--action-sheet-viewport-height,100dvh)_-_1rem_-_env(safe-area-inset-top,0px))]',
                    'data-[dragging]:select-none',
                    !isPresent && 'pointer-events-none',
                    className
                )}
                style={{
                    bottom: 'var(--action-sheet-viewport-bottom, 0px)',
                    left: 'var(--action-sheet-viewport-left, 0px)',
                    width: 'var(--action-sheet-viewport-width, 100%)',
                    zIndex: panelZIndex,
                }}
            >
                <div className="flex shrink-0 justify-center pt-3 pb-2" aria-hidden="true">
                    <div className="h-1 w-12 rounded-full bg-gray-300 dark:bg-gray-700"/>
                </div>
                {children}
            </div>
        </>
    )
}
